/**
 * CTA Buttons Component
 *
 * @package
 */

import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { copyToClipboard } from '../helpers/copyToClipboard';
import { downloadConfigurationPdf } from '../helpers/downloadConfigurationPdf';
import ShareableCodeModal from './ShareableCodeModal';
import EnquiryModal from './EnquiryModal';
import Toast from './Toast';
import Icon from './Icon';

const CTAButtons = ( { shareableCode, expirationDate, selectedBaseModel, selectedProducts, getShareableUrl, vehicleConfigId, hideDealersSection, vatMode, quantities = {} } ) => {
	const [ isDownloadingPdf, setIsDownloadingPdf ] = useState( false );
	const [ showCodeModal, setShowCodeModal ] = useState( false );
	const [ showEnquiryModal, setShowEnquiryModal ] = useState( false );
	const [ toast, setToast ] = useState( null );
	const [ codeUrl, setCodeUrl ] = useState( null );

	const scrollToDealers = () => {
		const dealersElement = document.querySelector( '.configurator-dealers' );
		if ( dealersElement ) {
			const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
			dealersElement.scrollIntoView( { behavior: prefersReducedMotion ? 'auto' : 'smooth' } );
		}
	};

	const showToast = ( message, type = 'success' ) => {
		setToast( { message, type } );
	};

	const handleCopyLink = async () => {
		let copied = false;

		try {
			copied = await copyToClipboard( shareableCode ? getShareableUrl( shareableCode ) : '' );
		} catch ( error ) {
			// getShareableUrl threw; fall through to the error toast.
		}

		showToast(
			copied
				? __( 'Link copied to clipboard', 'at-vehicle-configurator' )
				: __( 'Could not copy link', 'at-vehicle-configurator' ),
			copied ? 'success' : 'error'
		);
	};

	const handleDownloadPdf = async () => {
		setIsDownloadingPdf( true );

		try {
			await downloadConfigurationPdf( {
				selectedBaseModel,
				selectedProducts,
				vehicleConfigId,
				shareableCode,
				vatMode,
				quantities,
			} );
		} catch ( error ) {
			showToast( __( 'Error generating PDF. Please try again.', 'at-vehicle-configurator' ), 'error' );
		} finally {
			setIsDownloadingPdf( false );
		}
	};

	const handleCopyCodeLink = () => {
		showToast( __( 'Link copied to clipboard', 'at-vehicle-configurator' ), 'success' );
	};

	const handleOpenCodeModal = () => {
		setCodeUrl( shareableCode ? getShareableUrl( shareableCode ) : null );
		setShowCodeModal( true );
	};

	return (
		<>
			<div className="configurator-cta-buttons">
				<button
					type="button"
					onClick={ hideDealersSection ? () => setShowEnquiryModal( true ) : scrollToDealers }
					className="configurator-cta-button-primary"
				>
					{ hideDealersSection
						? __( 'Send your enquiry to Arctic Trucks', 'at-vehicle-configurator' )
						: __( 'Contact Dealer', 'at-vehicle-configurator' ) }
					<Icon name="arrow-right" width={ 17 } height={ 15 } className="configurator-cta-button-icon" />
				</button>
				<div className="configurator-cta-buttons-utility">
					<button
						type="button"
						onClick={ handleOpenCodeModal }
						className="configurator-button-utility"
						disabled={ ! shareableCode }
					>
						<Icon name="plus" size="medium" className="configurator-button-utility-icon" />
						{ __( 'Share Configuration', 'at-vehicle-configurator' ) }
					</button>
					<button
						type="button"
						onClick={ handleDownloadPdf }
						className="configurator-button-utility"
						disabled={ isDownloadingPdf }
					>
						<Icon name="download" width={ 17 } height={ 17 } className="configurator-button-utility-icon" />
						{ isDownloadingPdf
							? __( 'Generating…', 'at-vehicle-configurator' )
							: __( 'Download PDF', 'at-vehicle-configurator' ) }
					</button>
					<button
						type="button"
						onClick={ handleCopyLink }
						className="configurator-button-utility"
						disabled={ ! shareableCode }
					>
						<Icon name="share" size="medium" className="configurator-button-utility-icon" />
						{ __( 'Copy Link', 'at-vehicle-configurator' ) }
					</button>
				</div>
				{ showCodeModal && (
					<ShareableCodeModal
						onClose={ () => setShowCodeModal( false ) }
						shareableUrl={ codeUrl }
						expirationDate={ expirationDate }
						onCopyLink={ handleCopyCodeLink }
					/>
				) }
				{ hideDealersSection && showEnquiryModal && (
					<EnquiryModal
						isOpen={ showEnquiryModal }
						onClose={ () => setShowEnquiryModal( false ) }
						selectedDealer={ null }
						vehicleConfigId={ vehicleConfigId }
						selectedBaseModel={ selectedBaseModel }
						selectedProducts={ selectedProducts }
						shareableCode={ shareableCode }
						vatMode={ vatMode }
						quantities={ quantities }
						onSuccess={ () => setShowEnquiryModal( false ) }
					/>
				) }
			</div>
			{ toast && (
				<div className="configurator-toast-container">
					<Toast
						message={ toast.message }
						type={ toast.type }
						onDismiss={ () => setToast( null ) }
					/>
				</div>
			) }
		</>
	);
};

export default CTAButtons;
